<template>
<div>
    <!-- HERO -->
    <section class="pt-12 pt-md-14 mt-n11 bg-primary">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-8">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb breadcrumb-dark mb-2">
                            <li class="breadcrumb-item"><router-link to="/docs" class="text-white-80">Docs</router-link></li>
                            <li class="breadcrumb-item active text-white" aria-current="page">Dataset Builder</li>
                        </ol>
                    </nav>
                    <h1 class="display-3 fw-bold text-white">Dataset Builder Documentation</h1>
                    <p class="lead text-white-80 mb-0">
                        Build custom datasets by collecting fresh data based on your specific criteria.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- CONTENT -->
    <section class="py-8 py-md-11">
        <div class="container">
            <div class="row">
                <!-- SIDEBAR NAV -->
                <div class="col-12 col-lg-3 mb-8 mb-lg-0">
                    <div class="docs-nav-sidebar">
                        <h6 class="text-uppercase text-muted mb-3">On this page</h6>
                        <nav class="nav flex-column">
                            <a href="#overview" class="nav-link px-0">Overview</a>
                            <a href="#architecture" class="nav-link px-0">Architecture</a>
                            <a href="#dataset-types" class="nav-link px-0">Dataset Types</a>
                            <a href="#parameters" class="nav-link px-0">Parameters</a>
                            <a href="#pricing" class="nav-link px-0">Pricing</a>
                            <a href="#statuses" class="nav-link px-0">Statuses</a>
                            <a href="#api" class="nav-link px-0">API Integration</a>
                        </nav>
                    </div>
                </div>

                <!-- MAIN CONTENT -->
                <div class="col-12 col-lg-9">
                    <!-- OVERVIEW -->
                    <div id="overview" class="docs-section">
                        <h2 class="fw-bold">Overview</h2>
                        <p class="text-muted fs-lg">
                            Dataset Builder lets you collect custom Instagram data based on specific criteria.
                            Unlike pre-built datasets, the data is collected fresh at the time of your request,
                            ensuring you get the most up-to-date information.
                        </p>

                        <div class="row mt-4">
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Pre-built Datasets</h5>
                                        <ul class="text-muted mb-0">
                                            <li>Random sample from database</li>
                                            <li>Data may be 1-7 days old</li>
                                            <li>Instant download</li>
                                            <li>Fixed pricing</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-primary border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Dataset Builder</h5>
                                        <ul class="text-white-80 mb-0">
                                            <li>Targeted data collection</li>
                                            <li>Fresh data (collected now)</li>
                                            <li>Processing time varies</li>
                                            <li>Pay per row collected</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ARCHITECTURE -->
                    <div id="architecture" class="docs-section">
                        <h2 class="fw-bold">Architecture</h2>
                        <p class="text-muted">
                            Dataset Builder uses a queue-based architecture with worker processes that collect data from external sources.
                        </p>
                        <div class="docs-diagram mt-4">
                            <pre class="mb-0 text-white-80">
┌─────────────────────────────────────────────────────────────────────────┐
│                        Dataset Builder Flow                              │
└─────────────────────────────────────────────────────────────────────────┘

┌──────────┐    ┌──────────┐    ┌──────────────┐    ┌──────────────┐
│ 1. Order │───▶│ 2. Queue │───▶│ 3. Collect   │───▶│ 4. Generate  │
│          │    │          │    │    Data      │    │    CSV       │
└──────────┘    └──────────┘    └──────────────┘    └──────────────┘
                                       │
    User submits     Job waits    Datasetter        Final CSV
    request with     in queue     workers fetch     ready for
    parameters                    from Instagram    download

                     Status Flow:
              New → Working → Process → Done
                                    ↘ Aborted (if error)</pre>
                        </div>

                        <div class="alert alert-primary mt-4">
                            <strong>Processing time:</strong> Depends on dataset size and current queue.
                            Small datasets (1K-10K rows) typically complete in 5-30 minutes.
                            Large datasets may take several hours.
                        </div>
                    </div>

                    <!-- DATASET TYPES -->
                    <div id="dataset-types" class="docs-section">
                        <h2 class="fw-bold">Dataset Types</h2>

                        <div class="row mt-4">
                            <!-- Users -->
                            <div class="col-12 mb-4">
                                <div class="card bg-dark border-0">
                                    <div class="card-body">
                                        <h4 class="text-white">User Dataset</h4>
                                        <p class="text-muted">
                                            Collect Instagram user profiles matching your criteria.
                                        </p>

                                        <h6 class="text-primary-desat mt-4">Collection methods:</h6>
                                        <div class="table-responsive">
                                            <table class="table table-dark docs-table mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Method</th>
                                                        <th>Description</th>
                                                        <th>Example</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td><code>hashtag</code></td>
                                                        <td>Users who posted with hashtag</td>
                                                        <td>#fitness, #travel</td>
                                                    </tr>
                                                    <tr>
                                                        <td><code>location</code></td>
                                                        <td>Users who posted from location</td>
                                                        <td>New York, Dubai</td>
                                                    </tr>
                                                    <tr>
                                                        <td><code>followers</code></td>
                                                        <td>Followers of specific account</td>
                                                        <td>@nike followers</td>
                                                    </tr>
                                                    <tr>
                                                        <td><code>following</code></td>
                                                        <td>Accounts followed by user</td>
                                                        <td>Who @nike follows</td>
                                                    </tr>
                                                    <tr>
                                                        <td><code>commenters</code></td>
                                                        <td>Users who commented on posts</td>
                                                        <td>Comments on viral post</td>
                                                    </tr>
                                                    <tr>
                                                        <td><code>likers</code></td>
                                                        <td>Users who liked posts</td>
                                                        <td>Likers of @nike posts</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Posts -->
                            <div class="col-12 mb-4">
                                <div class="card bg-dark border-0">
                                    <div class="card-body">
                                        <h4 class="text-white">Post Dataset</h4>
                                        <p class="text-muted">
                                            Collect Instagram posts (photos, videos, reels) matching your criteria.
                                        </p>

                                        <h6 class="text-primary-desat mt-4">Collection methods:</h6>
                                        <div class="table-responsive">
                                            <table class="table table-dark docs-table mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Method</th>
                                                        <th>Description</th>
                                                        <th>Example</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td><code>hashtag</code></td>
                                                        <td>Posts with specific hashtag</td>
                                                        <td>Recent #marketing posts</td>
                                                    </tr>
                                                    <tr>
                                                        <td><code>location</code></td>
                                                        <td>Posts from location</td>
                                                        <td>Posts from Times Square</td>
                                                    </tr>
                                                    <tr>
                                                        <td><code>user</code></td>
                                                        <td>All posts from user(s)</td>
                                                        <td>@nike last 100 posts</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- PARAMETERS -->
                    <div id="parameters" class="docs-section">
                        <h2 class="fw-bold">Parameters</h2>
                        <p class="text-muted mb-4">
                            Configure your dataset with these parameters:
                        </p>

                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Parameter</th>
                                        <th>Required</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>kind</code></td>
                                        <td><span class="badge bg-danger">Yes</span></td>
                                        <td>Dataset type: <code>User</code> or <code>Post</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>method</code></td>
                                        <td><span class="badge bg-danger">Yes</span></td>
                                        <td>Collection method (hashtag, location, followers, etc.)</td>
                                    </tr>
                                    <tr>
                                        <td><code>target</code></td>
                                        <td><span class="badge bg-danger">Yes</span></td>
                                        <td>Hashtag name, location ID, or username</td>
                                    </tr>
                                    <tr>
                                        <td><code>limit</code></td>
                                        <td><span class="badge bg-warning text-dark">Optional</span></td>
                                        <td>Maximum rows to collect (default: 10000)</td>
                                    </tr>
                                    <tr>
                                        <td><code>min_followers</code></td>
                                        <td><span class="badge bg-warning text-dark">Optional</span></td>
                                        <td>Minimum follower count filter (User datasets)</td>
                                    </tr>
                                    <tr>
                                        <td><code>max_followers</code></td>
                                        <td><span class="badge bg-warning text-dark">Optional</span></td>
                                        <td>Maximum follower count filter</td>
                                    </tr>
                                    <tr>
                                        <td><code>is_business</code></td>
                                        <td><span class="badge bg-warning text-dark">Optional</span></td>
                                        <td>Filter by business account status</td>
                                    </tr>
                                    <tr>
                                        <td><code>has_email</code></td>
                                        <td><span class="badge bg-warning text-dark">Optional</span></td>
                                        <td>Only users with public email</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- PRICING -->
                    <div id="pricing" class="docs-section">
                        <h2 class="fw-bold">Pricing</h2>
                        <p class="text-muted">
                            Dataset Builder uses dynamic per-row pricing based on the collection method complexity:
                        </p>

                        <div class="table-responsive mt-4">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Collection Method</th>
                                        <th>Price per 1000 rows</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Hashtag users/posts</td>
                                        <td>$0.50 - $1.00</td>
                                        <td>Fast collection</td>
                                    </tr>
                                    <tr>
                                        <td>Location users/posts</td>
                                        <td>$0.50 - $1.00</td>
                                        <td>Fast collection</td>
                                    </tr>
                                    <tr>
                                        <td>Followers/Following</td>
                                        <td>$1.00 - $2.00</td>
                                        <td>Rate limited</td>
                                    </tr>
                                    <tr>
                                        <td>Likers/Commenters</td>
                                        <td>$1.50 - $3.00</td>
                                        <td>Most complex</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="alert alert-primary mt-4">
                            <strong>How billing works:</strong>
                            <ol class="mb-0 mt-2">
                                <li>Estimated cost is calculated and held from your balance</li>
                                <li>Collection runs until limit reached or no more data</li>
                                <li>Final charge based on actual rows collected</li>
                                <li>Difference is refunded if fewer rows collected</li>
                            </ol>
                        </div>
                    </div>

                    <!-- STATUSES -->
                    <div id="statuses" class="docs-section">
                        <h2 class="fw-bold">Dataset Statuses</h2>
                        <p class="text-muted mb-4">
                            Track your dataset progress through these statuses:
                        </p>

                        <div class="docs-diagram">
                            <pre class="mb-0 text-white-80">
     ┌───────┐      ┌─────────┐      ┌─────────┐      ┌──────┐
     │  New  │─────▶│ Working │─────▶│ Process │─────▶│ Done │
     └───────┘      └─────────┘      └─────────┘      └──────┘
                                           │
                                           ▼
                                     ┌─────────┐
                                     │ Aborted │
                                     └─────────┘</pre>
                        </div>

                        <div class="table-responsive mt-4">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Badge</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>New</td>
                                        <td><span class="badge bg-secondary">N</span></td>
                                        <td>Dataset created, waiting in queue</td>
                                    </tr>
                                    <tr>
                                        <td>Working</td>
                                        <td><span class="badge bg-info">W</span></td>
                                        <td>Worker assigned, collecting data</td>
                                    </tr>
                                    <tr>
                                        <td>Process</td>
                                        <td><span class="badge bg-warning text-dark">P</span></td>
                                        <td>Collection done, generating CSV</td>
                                    </tr>
                                    <tr>
                                        <td>Done</td>
                                        <td><span class="badge bg-success">D</span></td>
                                        <td>Complete, ready for download</td>
                                    </tr>
                                    <tr>
                                        <td>Charged</td>
                                        <td><span class="badge bg-primary">C</span></td>
                                        <td>Final charge applied</td>
                                    </tr>
                                    <tr>
                                        <td>Aborted</td>
                                        <td><span class="badge bg-danger">A</span></td>
                                        <td>Error or cancelled, funds returned</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- API -->
                    <div id="api" class="docs-section">
                        <h2 class="fw-bold">API Integration</h2>
                        <p class="text-muted">
                            Create datasets programmatically using our API:
                        </p>

                        <h5 class="mt-4">Create Dataset</h5>
                        <div class="docs-code-block">
                            <pre><code>POST /api/datasets/
Content-Type: application/json
x-access-key: YOUR_TOKEN

{
  "kind": "User",
  "method": "hashtag",
  "target": "fitness",
  "limit": 10000,
  "params": {
    "min_followers": 1000,
    "is_business": true
  }
}</code></pre>
                        </div>

                        <h5 class="mt-4">Check Status</h5>
                        <div class="docs-code-block">
                            <pre><code>GET /api/datasets/{dataset_id}/
x-access-key: YOUR_TOKEN

Response:
{
  "id": 12345,
  "status": "Done",
  "row_count": 9876,
  "download_url": "https://..."
}</code></pre>
                        </div>

                        <h5 class="mt-4">List Datasets</h5>
                        <div class="docs-code-block">
                            <pre><code>GET /api/datasets/
x-access-key: YOUR_TOKEN

Response:
{
  "results": [
    {"id": 12345, "kind": "User", "status": "Done", ...},
    {"id": 12344, "kind": "Post", "status": "Working", ...}
  ]
}</code></pre>
                        </div>
                    </div>

                    <!-- NEXT STEPS -->
                    <div class="mt-5 pt-5 border-top border-secondary">
                        <h4>Next Steps</h4>
                        <div class="row mt-4">
                            <!-- TODO(2026-06-25): hidden until DatasetBuilder is finished (see 2026-06-25-dataset-builder-status.md) -->
                            <div class="col-12 col-md-6 mb-3" v-if="false">
                                <router-link :to="{path: '/datasets/builder'}" class="card bg-primary border-0 h-100 text-decoration-none lift" v-if="$state.context.id">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Create Dataset</h6>
                                        <p class="text-white-80 small mb-0">Open Dataset Builder in Console</p>
                                    </div>
                                </router-link>
                                <router-link :to="{path: '/registration'}" class="card bg-primary border-0 h-100 text-decoration-none lift" v-else>
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Get Started</h6>
                                        <p class="text-white-80 small mb-0">Create account to use Dataset Builder</p>
                                    </div>
                                </router-link>
                            </div>
                            <div class="col-12 col-md-6 mb-3">
                                <router-link :to="{path: '/docs/datasets'}" class="card bg-dark border-0 h-100 text-decoration-none lift">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Pre-built Datasets</h6>
                                        <p class="text-muted small mb-0">Download instant datasets instead</p>
                                    </div>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
export default {
    created() {
        this.$methods.buildTitle('Dataset Builder Documentation')
    },
    mounted() {
        if (this.$route.hash) {
            this.$nextTick(() => {
                const element = document.querySelector(this.$route.hash)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                }
            })
        }
    }
}
</script>
